import type * as React from 'react'
import { NetworkStatus, useQuery } from '@apollo/client'
import Masonry from 'react-masonry-css'
import {
  EmptyState,
  PlusIcon,
  Button,
  CriticalIcon,
  VisuallyHidden,
  Link,
} from '@buffer-mono/popcorn'

import { graphql } from '~publish/gql'
import { useQueryParam } from '~publish/hooks/useQueryParam'
import { useInfiniteScrollPagination } from '~publish/hooks/useInfiniteScrollPagination'
import type { Idea as IdeaType } from '~publish/pages/Create/types'
import { useIdeaComposer } from '~publish/components/IdeaManagementRouter'
import { useOrganizationId } from '~publish/contexts/AccountContext'

import { IdeaCard, type IdeaCardSkeletonProps } from '../IdeaCard'
import { useDeleteIdea } from '../../hooks/useDeleteIdea'
import { CreateUploadDropzone } from '../CreateUploadDropzone'
import { useIdeasMultiSelect } from '../IdeasMultiSelect'

import styles from './IdeasGallery.module.css'

export const GetIdeasGallery = graphql(/* GraphQL */ `
  query GetIdeasGallery(
    $first: Int!
    $after: String
    $tags: [ID!]
    $groups: [ID!]
    $organizationId: ID!
  ) {
    ideasV2(
      input: { organizationId: $organizationId, tags: $tags, groups: $groups }
      first: $first
      after: $after
    ) {
      edges {
        node {
          id
          ...IdeaCard_Idea
        }
        cursor
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`)

// this is to keep the same order of skeletons between rerenders
const skeletonAssortment: Array<IdeaCardSkeletonProps['variant']> = [
  3, 1, 2, 4, 3, 1, 2, 3, 2, 1, 4, 1, 3,
]

const renderIdeaSkeletons = (length: number): JSX.Element[] => {
  const ideaSkeletons = Array.from(
    { length },
    (_, index) => skeletonAssortment[index % skeletonAssortment.length],
  )
  return ideaSkeletons.map((variant, index) => (
    <IdeaCard.Skeleton key={index} variant={variant} />
  ))
}

const IDEAS_PER_PAGE = 40

export const IdeasGallery = ({
  onDrop,
  onPaste,
}: {
  onDrop?: (files: File[], groupId?: string) => void
  onPaste?: (files: File[], groupId?: string) => void
}): JSX.Element => {
  const organizationId = useOrganizationId()
  const [selectedTagIds] = useQueryParam<string[]>('tagIds')
  const [selectedGroupIds] = useQueryParam<string[]>('groupIds')
  const { selecting, selectedIds, selectIdea } = useIdeasMultiSelect()

  const { createIdeaWithComposer, editIdeaWithComposer } = useIdeaComposer()

  const { deleteIdea } = useDeleteIdea()

  const { data, loading, error, fetchMore, networkStatus } = useQuery(
    GetIdeasGallery,
    {
      variables: {
        first: IDEAS_PER_PAGE,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        organizationId: organizationId!,
        tags: selectedTagIds,
        groups: selectedGroupIds,
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      skip: !organizationId,
    },
  )

  const [lastElementRef] = useInfiniteScrollPagination({
    loading: loading || networkStatus === NetworkStatus.fetchMore,
    hasNextPage: data?.ideasV2.pageInfo.hasNextPage,
    fetchMore: async () => {
      await fetchMore({
        variables: {
          after: data?.ideasV2.pageInfo.endCursor,
        },
      })
    },
  })

  if (loading) {
    return (
      <div className={styles.wrapper}>
        <VisuallyHidden role="progressbar">Loading ideas...</VisuallyHidden>
        <IdeasMasonryLayout>
          {renderIdeaSkeletons(IDEAS_PER_PAGE)}
        </IdeasMasonryLayout>
      </div>
    )
  }

  if (error) {
    return (
      <EmptyState variant="critical">
        <EmptyState.Icon>
          <CriticalIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>Error while loading ideas</EmptyState.Heading>
        <EmptyState.Description>
          Try to refresh the page, contact our team if the problem persists.
        </EmptyState.Description>
      </EmptyState>
    )
  }

  const ideas = data?.ideasV2?.edges?.map(({ node: idea }) => idea) ?? []
  const hasNextPage = data?.ideasV2?.pageInfo?.hasNextPage

  if (!ideas.length) {
    return (
      <CreateUploadDropzone onDrop={onDrop} onPaste={onPaste}>
        <EmptyState className={styles.emptyStateWrapper} size="xlarge">
          <EmptyState.Illustration src="https://buffer-publish.s3.amazonaws.com/images/empty-ideas-gallery-illustration.png" />
          <EmptyState.Heading>A space to save your ideas</EmptyState.Heading>
          <EmptyState.Description>
            Create a new Idea or drag and drop images or videos here.
          </EmptyState.Description>
          <EmptyState.Actions>
            <Button
              size="large"
              onClick={(): void =>
                createIdeaWithComposer({
                  source: 'create-gallery-emptyState-button-1',
                })
              }
            >
              <PlusIcon /> New Idea
            </Button>
          </EmptyState.Actions>
          <Link
            href="https://support.buffer.com/article/589-creating-ideas-in-buffer"
            external
          >
            Learn about Ideas
          </Link>
        </EmptyState>
      </CreateUploadDropzone>
    )
  }

  return (
    <CreateUploadDropzone
      className={styles.wrapper}
      onDrop={onDrop}
      onPaste={onPaste}
    >
      <div
        className={styles.feed}
        role="feed"
        aria-busy={networkStatus === NetworkStatus.fetchMore}
      >
        <IdeasMasonryLayout>
          {ideas.map((idea, index) => (
            <IdeaCard
              key={idea.id}
              // add ref to last element to trigger pagination
              ref={
                hasNextPage && index === ideas.length - 1
                  ? lastElementRef
                  : null
              }
              idea={idea as IdeaType}
              selectable={selecting}
              selected={selectedIds.includes(idea.id)}
              onSelect={selectIdea}
              onOpen={(idea): void =>
                editIdeaWithComposer({
                  source: 'create-gallery-ideaCard-card-1',
                  ideaId: idea.id,
                })
              }
              onDelete={(idea): Promise<void> => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error Idea and IdeaFragment are not compatible because of null
                return deleteIdea(idea, {
                  source: 'create-gallery-ideaCard-delete-1',
                })
              }}
            />
          ))}
          {hasNextPage && renderIdeaSkeletons(IDEAS_PER_PAGE)}
        </IdeasMasonryLayout>
      </div>
    </CreateUploadDropzone>
  )
}

IdeasGallery.displayName = 'IdeasGallery'

const IdeasMasonryLayout = ({
  children,
}: {
  children: React.ReactNode
}): JSX.Element => {
  return (
    // TODO: Masonry implementation creates unnatural focus order
    // we need to either use different layout or change the implemanetation
    // to calculate positions with javascript
    <Masonry
      breakpointCols={{
        default: 7,
        1800: 6,
        1580: 5,
        1300: 4,
        1120: 3,
        860: 2,
        560: 1,
      }}
      className={styles.masonry}
      columnClassName={styles.column}
    >
      {children}
    </Masonry>
  )
}

IdeasMasonryLayout.displayName = 'IdeasMasonryLayout'
